/**
 * LLM Usage Admin Page
 * 
 * Shows platform-wide LLM usage statistics and cost tracking for master admins.
 * Updated to match dark theme styling consistent with Billing & Finance page.
 */

import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { motion } from 'framer-motion';
import {
  ArrowsClockwise,
  Buildings,
  Clock,
  CurrencyDollar,
  FileText,
  Funnel,
  Pulse,
  Robot,
  TrendUp,
  WarningCircle
} from '@phosphor-icons/react';

export default function LLMUsageAdmin() {
  const [summary, setSummary] = useState(null);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [orgUsage, setOrgUsage] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState("30");
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setError('Not authenticated. Please login again.');
      setLoading(false);
      return;
    }
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    try {
      // Fetch summary
      const summaryRes = await fetch(`${BACKEND_URL}/api/admin/llm-usage/summary?days=${days}`, { headers });
      if (!summaryRes.ok) throw new Error('Failed to fetch summary');
      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      // Fetch daily usage
      const dailyRes = await fetch(`${BACKEND_URL}/api/admin/llm-usage/daily?days=${days}`, { headers });
      if (dailyRes.ok) {
        const dailyData = await dailyRes.json();
        setDailyUsage(dailyData.daily_usage || []);
      }

      // Fetch org breakdown
      const orgRes = await fetch(`${BACKEND_URL}/api/admin/llm-usage/by-organization?days=${days}`, { headers });
      if (orgRes.ok) {
        const orgData = await orgRes.json();
        setOrgUsage(orgData.organizations || []);
      }

      // Fetch recent logs
      const logsRes = await fetch(`${BACKEND_URL}/api/admin/llm-usage/logs?page_size=20`, { headers });
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData.logs || []);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [days]);

  const formatCost = (cost) => {
    if (cost === undefined || cost === null) return '$0.0000';
    return `$${cost.toFixed(4)}`;
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 text-red-400">
            <WarningCircle className="w-5 h-5" />
            <span>Error loading LLM usage data: {error}</span>
          </div>
          <button 
            onClick={fetchData} 
            className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'by-model', label: 'By Model' },
    { id: 'by-purpose', label: 'By Purpose' },
    { id: 'by-org', label: 'By Organization' },
    { id: 'logs', label: 'Logs' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">LLM Usage & Cost Tracking</h1>
          <p className="text-gray-400">Monitor platform-wide AI usage and optimize costs</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button 
            onClick={fetchData} 
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 rounded-lg transition-colors text-white"
          >
            <ArrowsClockwise className="w-4 h-4" />
            ArrowsClockwise
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <CurrencyDollar size={24} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Cost (Est.)</p>
              <p className="text-2xl font-bold text-white">{formatCost(summary?.total_cost_usd)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Last {days} days</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Pulse size={24} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Calls</p>
              <p className="text-2xl font-bold text-white">{formatNumber(summary?.total_calls)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-green-400">{formatNumber(summary?.successful_calls)} success</span>
            <span className="text-gray-500">|</span>
            <span className="text-red-400">{formatNumber(summary?.failed_calls)} failed</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <FileText size={24} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Tokens</p>
              <p className="text-2xl font-bold text-white">{formatNumber(summary?.total_tokens)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Input + Output</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <Buildings size={24} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Active Orgs</p>
              <p className="text-2xl font-bold text-white">{orgUsage.length}</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">With LLM usage</p>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'text-[#6366F1] border-b-2 border-[#6366F1]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Model Breakdown */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-2">Usage by Model</h3>
            <p className="text-sm text-gray-400 mb-4">Token consumption and cost by model</p>
            <div className="space-y-3">
              {Object.entries(summary.tokens_by_model || {}).map(([model, data]) => (
                <div key={model} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="font-medium text-white">{model}</p>
                    <p className="text-sm text-gray-400">{formatNumber(data.calls)} calls</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-white">{formatNumber(data.tokens)} tokens</p>
                    <p className="text-sm text-green-400">{formatCost(data.cost)}</p>
                  </div>
                </div>
              ))}
              {Object.keys(summary.tokens_by_model || {}).length === 0 && (
                <p className="text-center text-gray-500 py-4">No usage data yet</p>
              )}
            </div>
          </div>

          {/* Purpose Breakdown */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-2">Usage by Purpose</h3>
            <p className="text-sm text-gray-400 mb-4">What features are using LLM calls</p>
            <div className="space-y-3">
              {Object.entries(summary.calls_by_purpose || {}).map(([purpose, data]) => (
                <div key={purpose} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="font-medium text-white capitalize">{purpose.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-white">{formatNumber(data.calls)} calls</p>
                    <p className="text-sm text-gray-400">{formatNumber(data.tokens)} tokens</p>
                  </div>
                </div>
              ))}
              {Object.keys(summary.calls_by_purpose || {}).length === 0 && (
                <p className="text-center text-gray-500 py-4">No usage data yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'by-org' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-white font-semibold">Usage by Organization</h3>
            <p className="text-sm text-gray-400">Cost allocation across organizations</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-400 uppercase">Organization</th>
                  <th className="text-right py-3 px-6 text-xs font-medium text-gray-400 uppercase">Calls</th>
                  <th className="text-right py-3 px-6 text-xs font-medium text-gray-400 uppercase">Tokens</th>
                  <th className="text-right py-3 px-6 text-xs font-medium text-gray-400 uppercase">Est. Cost</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-400 uppercase">Models Used</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {orgUsage.map((org) => (
                  <tr key={org.org_id} className="hover:bg-white/5">
                    <td className="py-4 px-6">
                      <p className="font-medium text-white">{org.org_name}</p>
                      <p className="text-xs text-gray-500">{org.org_id}</p>
                    </td>
                    <td className="py-4 px-6 text-right text-white">{formatNumber(org.total_calls)}</td>
                    <td className="py-4 px-6 text-right text-white">{formatNumber(org.total_tokens)}</td>
                    <td className="py-4 px-6 text-right font-medium text-green-400">{formatCost(org.total_cost_usd)}</td>
                    <td className="py-4 px-6">
                      <div className="flex flex-wrap gap-1">
                        {org.models_used?.map((model) => (
                          <span key={model} className="px-2 py-0.5 text-xs bg-[#6366F1]/20 text-[#6366F1] rounded-full">
                            {model}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {orgUsage.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      No organization usage data yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-white font-semibold">Recent LLM Calls</h3>
            <p className="text-sm text-gray-400">Detailed log of recent API calls</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Time</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Purpose</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase">Model</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-400 uppercase">Tokens</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-400 uppercase">Cost</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {logs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-white/5">
                    <td className="py-3 px-4 text-gray-400">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-white capitalize">{log.purpose?.replace(/_/g, ' ')}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 text-xs bg-white/10 text-gray-300 rounded-full">{log.model}</span>
                    </td>
                    <td className="py-3 px-4 text-right text-white">{formatNumber(log.tokens_total)}</td>
                    <td className="py-3 px-4 text-right text-green-400">{formatCost(log.cost_estimate_usd)}</td>
                    <td className="py-3 px-4 text-center">
                      {log.success ? (
                        <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">Success</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">Failed</span>
                      )}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      No LLM calls logged yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(activeTab === 'by-model' || activeTab === 'by-purpose') && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <p className="text-center text-gray-400">
            Detailed breakdown available in the Overview tab above
          </p>
        </div>
      )}

      {/* Platform Default Notice */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
        <Robot className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-blue-400">Platform Default Model: gpt-4o-mini</p>
          <p className="text-sm text-blue-300/80 mt-1">
            All platform LLM calls now use the organization&apos;s configured model or default to gpt-4o-mini 
            for cost efficiency. Vision calls automatically use gpt-4o when required.
          </p>
        </div>
      </div>
    </div>
  );
}
